import { DATE_FIELDS } from "../../consts";
import { SEARCH_FIELDS } from "../../mappings";
import { formatSearchQuery } from "../../util";
import { FlowState, QueryGroup, QueryItem } from "./@types";
import { FLOW_STATES } from "./consts";
import {
  createNewGroupOption,
  createNewOption
} from "./utils/createNewOptionsUtils";

const SEARCH_KEYWORD_SET = new Set(Object.values(SEARCH_FIELDS));
const OPERATOR_KEYWORD_SET = new Set(["AND", "OR", "NOT"]);

const wrapValueInQuotesIfNecessary = (value: string) => {
  if (!value.startsWith('"') && !value.endsWith('"')) return `"${value}"`;
  return value;
};

const quoteElementsIFNecessary = (currentQueryGroup: QueryItem[]) => {
  const keywordGroup = currentQueryGroup[0];
  if (DATE_FIELDS.includes(keywordGroup?.value ?? "")) return currentQueryGroup;

  return currentQueryGroup.map(queryItem => {
    const { value, state } = queryItem;

    if (
      value.includes(":") &&
      (state !== FLOW_STATES.ALL_KEYWORDS || !SEARCH_KEYWORD_SET.has(value))
    ) {
      return {
        ...queryItem,
        value: wrapValueInQuotesIfNecessary(value)
      };
    }

    return queryItem;
  });
};

const createNewKeywordGroup = (currentQueryGroup: QueryItem[]) => {
  const quotedGroup = quoteElementsIFNecessary(currentQueryGroup);
  return createNewGroupOption({
    value: "",
    grouped: quotedGroup,
    keyword: quotedGroup[0]?.value
  });
};

export const parseQuery = (query: string): QueryGroup[] => {
  const { modified: formattedQuery } = formatSearchQuery(query);
  const queryItems: QueryGroup[] = [];
  let currentQueryGroup: QueryItem[] = [];
  let flowState: FlowState = FLOW_STATES.ALL_KEYWORDS;
  let currentString = "";
  let openedQuotes = false;

  const addNewGroupIfEndOfQueryGroup = () => {
    const slicedCurrentString = currentString.trim();
    if (slicedCurrentString)
      currentQueryGroup.push(
        createNewOption({ value: slicedCurrentString, flowState })
      );
    if (currentQueryGroup.length > 0) {
      queryItems.push(createNewKeywordGroup(currentQueryGroup));
      currentQueryGroup = [];
    }
    currentString = "";
    flowState = FLOW_STATES.ALL_KEYWORDS;
    return;
  };

  const autocapitalizeOperatorKeyword = () => {
    if (OPERATOR_KEYWORD_SET.has(currentString.trim().toUpperCase())) {
      currentString = currentString.trim().toUpperCase();
    }
  };

  formattedQuery.split("").forEach((char, index, array) => {
    if (flowState === FLOW_STATES.ALL_KEYWORDS) {
      currentString = `${currentString}${char}`;

      if (char === '"' && array[index - 1] !== "\\") {
        openedQuotes = !openedQuotes;
      }

      if (openedQuotes) return;

      currentString = currentString.trim();

      if (char === " ") {
        return;
      }

      const trimmedCurrentString = currentString.slice(0, -1).trim();

      if (array[index - 1] === " " && char !== ":" && !!trimmedCurrentString) {
        currentString = trimmedCurrentString;

        autocapitalizeOperatorKeyword();

        currentQueryGroup.push(
          createNewOption({
            value: currentString,
            flowState
          })
        );
        queryItems.push(createNewKeywordGroup(currentQueryGroup));
        currentString = char;
        currentQueryGroup = [];
      }

      if (char === ":" && SEARCH_KEYWORD_SET.has(currentString)) {
        currentQueryGroup.push(
          createNewOption({
            value: `${currentString.slice(0, -1).trim()}:`,
            flowState
          })
        );
        currentString = "";
        flowState = FLOW_STATES.SINGLE_OR_MULTI_VALUE;
        return;
      }

      if (currentString === "[") {
        currentQueryGroup.push(createNewOption({ value: "", flowState }));
        currentQueryGroup.push(
          createNewOption({
            value: char,
            flowState: FLOW_STATES.SINGLE_OR_MULTI_VALUE
          })
        );
        currentString = "";
        flowState = FLOW_STATES.MULTI_VALUE;
        return;
      }

      if (char === "(" || char === ")") {
        currentString = currentString.slice(0, -1);

        autocapitalizeOperatorKeyword();

        addNewGroupIfEndOfQueryGroup();

        queryItems.push(
          createNewGroupOption({
            value: char
          })
        );
        currentString = "";
        currentQueryGroup = [];

        return;
      }

      if (index === array.length - 1) {
        autocapitalizeOperatorKeyword();
        addNewGroupIfEndOfQueryGroup();
        return;
      }
    }

    if (flowState === FLOW_STATES.SINGLE_OR_MULTI_VALUE) {
      if (char === " " && currentString === "") return;

      currentString += char;

      if (currentString.trim() === "[") {
        currentQueryGroup.push(createNewOption({ value: "[", flowState }));
        flowState = FLOW_STATES.FIRST_MULTI_VALUE;
        currentString = "";
        return;
      }

      if (char === '"' && array[index - 1] !== "\\") {
        openedQuotes = !openedQuotes;
      }

      if (
        ([" ", "(", ")"].includes(char) && !openedQuotes) ||
        index === array.length - 1
      ) {
        const currentCharIsBracket = char === "(" || char === ")";
        const slicedCurrentString = currentCharIsBracket
          ? currentString.slice(0, -1).trim()
          : currentString;
        if (slicedCurrentString)
          currentQueryGroup.push(
            createNewOption({
              value: slicedCurrentString,
              flowState
            })
          );

        queryItems.push(createNewKeywordGroup(currentQueryGroup));
        if (currentCharIsBracket) {
          queryItems.push(
            createNewGroupOption({
              value: char
            })
          );
        }
        currentQueryGroup = [];
        currentString = "";
        flowState = FLOW_STATES.ALL_KEYWORDS;
        return;
      }
    }

    if (flowState === FLOW_STATES.FIRST_MULTI_VALUE) {
      currentString += char;

      if (char === '"' && array[index - 1] !== "\\") {
        openedQuotes = !openedQuotes;
      }

      if (currentString.endsWith(" TO") && !openedQuotes) {
        const slicedCurrentString = currentString.slice(0, -3).trim();
        if (slicedCurrentString)
          currentQueryGroup.push(
            createNewOption({ value: slicedCurrentString, flowState })
          );

        currentQueryGroup.push(
          createNewOption({
            value: "TO",
            flowState: FLOW_STATES.MULTI_VALUE_OR_RANGE
          })
        );
        flowState = FLOW_STATES.RANGETOVALUE;
        currentString = "";
        return;
      }

      if (char === "," && !openedQuotes) {
        const slicedCurrentString = currentString.slice(0, -1).trim();
        if (slicedCurrentString)
          currentQueryGroup.push(
            createNewOption({ value: slicedCurrentString, flowState })
          );

        flowState = FLOW_STATES.MULTI_VALUE;
        currentString = "";
        return;
      }

      if (char === "]" && !openedQuotes) {
        const slicedCurrentString = currentString.slice(0, -1).trim();
        if (slicedCurrentString)
          currentQueryGroup.push(
            createNewOption({ value: slicedCurrentString, flowState })
          );
        currentQueryGroup.push(
          createNewOption({
            value: "]",
            flowState: FLOW_STATES.MULTI_VALUE_OR_RANGE
          })
        );
        queryItems.push(createNewKeywordGroup(currentQueryGroup));
        currentQueryGroup = [];
        currentString = "";
        flowState = FLOW_STATES.ALL_KEYWORDS;
        return;
      }

      if (index === array.length - 1) {
        addNewGroupIfEndOfQueryGroup();
        return;
      }
    }

    if (flowState === FLOW_STATES.MULTI_VALUE) {
      currentString += char;

      if (char === '"' && array[index - 1] !== "\\") {
        openedQuotes = !openedQuotes;
      }

      if (char === "," && !openedQuotes) {
        const slicedCurrentString = currentString.slice(0, -1).trim();
        if (slicedCurrentString)
          currentQueryGroup.push(
            createNewOption({ value: slicedCurrentString, flowState })
          );
        currentString = "";
        return;
      }

      if (char === "]" && !openedQuotes) {
        const slicedCurrentString = currentString.slice(0, -1).trim();
        if (slicedCurrentString)
          currentQueryGroup.push(
            createNewOption({ value: slicedCurrentString, flowState })
          );
        currentQueryGroup.push(
          createNewOption({ value: "]", flowState: FLOW_STATES.MULTI_VALUE })
        );
        queryItems.push(createNewKeywordGroup(currentQueryGroup));
        currentQueryGroup = [];
        currentString = "";
        flowState = FLOW_STATES.ALL_KEYWORDS;
        return;
      }

      if (index === array.length - 1) {
        addNewGroupIfEndOfQueryGroup();
        return;
      }
    }

    if (flowState === FLOW_STATES.RANGETOVALUE) {
      currentString += char;

      if (char === '"' && array[index - 1] !== "\\") {
        openedQuotes = !openedQuotes;
      }

      if (char === "]" && !openedQuotes) {
        const slicedCurrentString = currentString.slice(0, -1).trim();
        if (slicedCurrentString)
          currentQueryGroup.push(
            createNewOption({ value: slicedCurrentString, flowState })
          );
        currentQueryGroup.push(
          createNewOption({ value: "]", flowState: FLOW_STATES.CLOSE_RANGE })
        );
        queryItems.push(createNewKeywordGroup(currentQueryGroup));
        currentQueryGroup = [];
        currentString = "";
        flowState = FLOW_STATES.ALL_KEYWORDS;
        return;
      }

      if (index === array.length - 1) {
        addNewGroupIfEndOfQueryGroup();
        return;
      }
    }
  });

  addNewGroupIfEndOfQueryGroup();

  return queryItems;
};
